import React from "react";
import { FilePlus2, Send, CheckCircle2, XCircle, Lock } from "lucide-react";
import { formatEventTime } from "../../utils/searchRequestFormat";

// Read-only chronological activity trail for a search request (DoD 28). The
// backend reconstructs it from existing audit timestamps (no audit table), so we
// only render — one light timeline row per event: created / sent / accepted /
// declined / closed. A detached member arrives as "Former member" (DoD 29).
const ICON = {
  created: { Icon: FilePlus2, color: "text-[#2283FF]", bg: "bg-[#E8F1FF]" },
  sent: { Icon: Send, color: "text-gray-500", bg: "bg-gray-100" },
  accepted: { Icon: CheckCircle2, color: "text-[#16A34A]", bg: "bg-[#ECFDF5]" },
  declined: { Icon: XCircle, color: "text-[#DC2626]", bg: "bg-[#FEE2E2]" },
  closed: { Icon: Lock, color: "text-gray-500", bg: "bg-gray-100" },
};

const label = (e) => {
  const who = e.agent?.display_name || e.by?.name || "";
  switch (e.type) {
    case "created":
      return (
        <span>
          Created{who ? <> by <span className="font-semibold text-[#374151]">{who}</span></> : ""}
        </span>
      );
    case "sent":
      return (
        <span>
          Sent to <span className="font-semibold text-[#374151]">{who}</span>
        </span>
      );
    case "accepted":
      return (
        <span>
          <span className="font-semibold text-[#374151]">{who}</span> accepted
        </span>
      );
    case "declined":
      return (
        <span>
          <span className="font-semibold text-[#374151]">{who}</span> declined
          {e.decline_reason ? (
            <span className="italic text-gray-400">: “{e.decline_reason}”</span>
          ) : null}
        </span>
      );
    case "closed":
      return (
        <span>
          Closed{who ? <> by <span className="font-semibold text-[#374151]">{who}</span></> : ""}
        </span>
      );
    default:
      return <span>{e.type}</span>;
  }
};

const ActivityTrail = ({ events }) => {
  if (!Array.isArray(events) || events.length === 0) return null;
  return (
    <div className="flex flex-col gap-0">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
        Activity
      </span>
      <ol className="flex flex-col">
        {events.map((e, i) => {
          const cfg = ICON[e.type] || ICON.sent;
          const { Icon } = cfg;
          const last = i === events.length - 1;
          return (
            <li key={`${e.type}-${e.at}-${i}`} className="flex gap-3">
              <div className="flex flex-col items-center flex-none">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center ${cfg.bg}`}>
                  <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                </span>
                {!last && <span className="w-px flex-1 bg-[#E5E7EB] my-1" />}
              </div>
              <div className={`flex flex-col ${last ? "" : "pb-4"}`}>
                <span className="text-[12.5px] text-[#4B5563]">{label(e)}</span>
                {e.at && (
                  <span className="text-[11px] text-gray-400">{formatEventTime(e.at)}</span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default ActivityTrail;
